import { BookOpen, House, RotateCcw, Settings, Trophy } from 'lucide-react';
import { Link, Navigate, NavLink, Outlet } from 'react-router';
import { Avatar } from '@/components/ui/Avatar';
import { Loading } from '@/components/ui/Loading';
import { useI18n } from '@/i18n/context';
import { cn } from '@/lib/cn';
import { useAppState } from '../useAppState';
import { BrandMark } from './BrandMark';
import { SkipLink } from './SkipLink';

/** Shell of the child area: requires an active learner, adds the navigation. */
export function ChildLayout() {
  const { ready, activeLearner } = useAppState();
  const { t } = useI18n();
  if (!ready) return <Loading label={t('app.loading')} />;
  if (!activeLearner) return <Navigate to="/profiles" replace />;

  const nav = [
    { to: '/learn', label: t('nav.home'), icon: House, end: true },
    { to: '/learn/subjects', label: t('nav.subjects'), icon: BookOpen, end: false },
    { to: '/learn/review', label: t('nav.review'), icon: RotateCcw, end: false },
    { to: '/learn/rewards', label: t('nav.rewards'), icon: Trophy, end: false },
  ];

  return (
    <div className="flex min-h-dvh flex-col">
      <SkipLink />
      <header className="no-print sticky top-0 z-20 border-b border-line/70 bg-canvas/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2">
          <BrandMark to="/learn" />
          <nav
            aria-label={t('app.mainNavigation')}
            className="ms-6 hidden flex-1 items-center gap-1 md:flex"
          >
            {nav.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'inline-flex min-h-11 items-center gap-2 rounded-full px-4 font-bold',
                    isActive
                      ? 'bg-brand-100 text-brand-800'
                      : 'text-ink-muted hover:bg-surface-muted',
                  )
                }
              >
                <Icon aria-hidden className="size-5" />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="ms-auto flex items-center gap-2">
            <Link
              to="/learn/settings"
              aria-label={t('nav.settings')}
              className="inline-flex size-11 items-center justify-center rounded-full text-ink-muted hover:bg-surface-muted"
            >
              <Settings aria-hidden className="size-6" />
            </Link>
            <Link to="/profiles" aria-label={t('profiles.switch')} className="rounded-full">
              <Avatar avatar={activeLearner.avatar} size="sm" />
            </Link>
          </div>
        </div>
      </header>

      <main
        id="main"
        tabIndex={-1}
        className="mx-auto w-full max-w-5xl flex-1 px-4 pt-6 pb-28 outline-none md:pb-12"
      >
        <Outlet />
      </main>

      <nav
        aria-label={t('app.mainNavigation')}
        className="no-print fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      >
        <ul className="mx-auto grid max-w-md grid-cols-4">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-16 flex-col items-center justify-center gap-0.5 text-xs font-bold',
                    isActive ? 'text-brand-700' : 'text-ink-muted',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={cn('rounded-full px-4 py-1', isActive && 'bg-brand-100')}>
                      <Icon aria-hidden className="size-6" />
                    </span>
                    {label}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/** Distraction-free shell for playing: no navigation bars. */
export function FocusLayout() {
  const { ready, activeLearner } = useAppState();
  const { t } = useI18n();
  if (!ready) return <Loading label={t('app.loading')} />;
  if (!activeLearner) return <Navigate to="/profiles" replace />;
  return (
    <main
      id="main"
      tabIndex={-1}
      className="mx-auto flex min-h-dvh w-full max-w-3xl flex-col px-4 py-4 outline-none"
    >
      <Outlet />
    </main>
  );
}
